import React, { useState, useEffect, useCallback } from 'react';
import Chart from 'react-apexcharts';
import { getAuthContext } from '../../utils/jwt';
import { toast } from 'react-toastify';
import { BASE_URL } from '../../utils/constants';

// Interface for campaign performance from API - matches actual response
interface CampaignPerformanceItem {
  id: string;
  name: string;
  value: number;
  delivered: number;
}

// Interface for top campaigns from API
interface TopCampaignsResponse {
  topCampaigns: TopCampaign[];
}

interface TopCampaign {
  campaignId: string;
  name: string;
  openRate: number;
  clickRate: number;
  conversionRate: number;
}

// Interface for automation performance from API - matches actual response
interface AutomationPerformanceItem {
  id: string;
  name: string;
  value: number;
  delivered: number;
}

// Interface for top automation performance from API - matches actual response
interface TopAutomationItem {
  id: string;
  name: string;
  value: number;
  label: string;
  delivered: number;
}

// Interface for top performing items display
interface TopPerformingItem {
  title: string;
  rate: string;
  id: string;
  value: number;
}

// AutomationSection component displays performance metrics for automations and campaigns using bar charts.
const AutomationSection: React.FC = () => {
  // Manages the active tab (automation or campaign)
  const [activeTab, setActiveTab] = useState<'automation' | 'campaign'>('automation');

  // Manages the selected time range for both campaign and automation
  const [selectedRange, setSelectedRange] = useState<'LAST1D' | 'LAST7D' | 'LAST30D'>('LAST1D');

  // State for campaign performance data
  const [campaignPerformance, setCampaignPerformance] = useState<CampaignPerformanceItem[]>([]);
  const [performanceLoading, setPerformanceLoading] = useState<boolean>(true);
  const [performanceError, setPerformanceError] = useState<string | null>(null);

  // State for top campaigns data
  const [topCampaigns, setTopCampaigns] = useState<TopCampaign[]>([]);
  const [topLoading, setTopLoading] = useState<boolean>(true);
  const [topError, setTopError] = useState<string | null>(null);

  // State for automation performance data
  const [automationPerformance, setAutomationPerformance] = useState<AutomationPerformanceItem[]>([]);
  const [automationLoading, setAutomationLoading] = useState<boolean>(true);
  const [automationError, setAutomationError] = useState<string | null>(null);

  // State for top automations data
  const [topAutomations, setTopAutomations] = useState<TopAutomationItem[]>([]);
  const [topAutomationsLoading, setTopAutomationsLoading] = useState<boolean>(true);
  const [topAutomationsError, setTopAutomationsError] = useState<string | null>(null);

  // Base URL for API calls

  // Function to get date range based on selected range
  // const getDateRange = useCallback(() => {
  //   const now = new Date();
  //   let from: Date;

  //   switch (selectedRange) {
  //     case 'LAST1D':
  //       from = new Date(now);
  //       from.setDate(from.getDate() - 1);
  //       break;
  //     case 'LAST7D':
  //       from = new Date(now);
  //       from.setDate(from.getDate() - 7);
  //       break;
  //     case 'LAST30D':
  //       from = new Date(now);
  //       from.setDate(from.getDate() - 30);
  //       break;
  //     default:
  //       from = new Date(now);
  //       from.setDate(from.getDate() - 1);
  //       break;
  //   }

  //   return {
  //     from: from.toISOString(),
  //     to: now.toISOString(),
  //   };
  // }, [selectedRange]);

  // Function to fetch automation performance data
  const fetchAutomationPerformance = useCallback(async () => {
    const { token, tenantId, userId } = getAuthContext();

    if (!token || !tenantId) {
      setAutomationError('Authentication required');
      setAutomationLoading(false);
      return;
    }

    setAutomationLoading(true);
    setAutomationError(null);

    try {
      // Construct the API URL with query parameters
      const url = `${BASE_URL}/v1/reporting/automation-performance?&userId=${userId}&range=${selectedRange}`;


      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'X-Tenant-Id': tenantId,
          'X-User-Id': userId || '',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data: AutomationPerformanceItem[] = await response.json();

      setAutomationPerformance(data || []);
    } catch (err: any) {
      // console.error('❌ Failed to fetch automation performance:', err);
      setAutomationError(err.message || 'Failed to load automation performance');
      toast.error('Failed to load automation performance');
      setAutomationPerformance([]);
    } finally {
      setAutomationLoading(false);
    }
  }, [selectedRange]);

  // Function to fetch top automations data
  const fetchTopAutomations = useCallback(async () => {
    const { token, tenantId, userId } = getAuthContext();

    if (!token || !tenantId) {
      setTopAutomationsError('Authentication required');
      setTopAutomationsLoading(false);
      return;
    }

    setTopAutomationsLoading(true);
    setTopAutomationsError(null);

    try {
      // Construct the API URL with query parameters
      const url = `${BASE_URL}/v1/reporting/automation-performance/top?&range=${selectedRange}&minDelivered=0`;


      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'X-Tenant-Id': tenantId,
          'X-User-Id': userId || '',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data: TopAutomationItem[] = await response.json();

      setTopAutomations(data || []);
    } catch (err: any) {
      // console.error('❌ Failed to fetch top automations:', err);
      setTopAutomationsError(err.message || 'Failed to load top automations');
      toast.error('Failed to load top automations');
      setTopAutomations([]);
    } finally {
      setTopAutomationsLoading(false);
    }
  }, [selectedRange]);

  // Function to fetch campaign performance data
  const fetchCampaignPerformance = useCallback(async () => {
    const { token, tenantId, userId } = getAuthContext();

    if (!token || !tenantId) {
      setPerformanceError('Authentication required');
      setPerformanceLoading(false);
      return;
    }

    setPerformanceLoading(true);
    setPerformanceError(null);

    try {
      // Construct the API URL with query parameters
      const url = `${BASE_URL}/v1/reporting/campaign-performance?grain=&range=${selectedRange}`;


      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'X-Tenant-Id': tenantId,
          'X-User-Id': userId || '',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data: CampaignPerformanceItem[] = await response.json();

      setCampaignPerformance(data || []);
    } catch (err: any) {
      // console.error('❌ Failed to fetch campaign performance:', err);
      setPerformanceError(err.message || 'Failed to load campaign performance');
      toast.error('Failed to load campaign performance');
      setCampaignPerformance([]);
    } finally {
      setPerformanceLoading(false);
    }
  }, [selectedRange]);

  // Function to fetch top campaigns data
  const fetchTopCampaigns = useCallback(async () => {
    const { token, tenantId, userId } = getAuthContext();

    if (!token || !tenantId) {
      setTopError('Authentication required');
      setTopLoading(false);
      return;
    }

    setTopLoading(true);
    setTopError(null);

    try {
      // Construct the API URL with query parameters
      const url = `${BASE_URL}/v1/reporting/campaign-performance/top?&range=${selectedRange}`;


      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'X-Tenant-Id': tenantId,
          'X-User-Id': userId || '',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data: TopCampaignsResponse = await response.json();

      setTopCampaigns(data.topCampaigns || []);
    } catch (err: any) {
      // console.error('❌ Failed to fetch top campaigns:', err);
      setTopError(err.message || 'Failed to load top campaigns');
      toast.error('Failed to load top campaigns');
      setTopCampaigns([]);
    } finally {
      setTopLoading(false);
    }
  }, [selectedRange]);

  // Fetch all data when component mounts or range changes
  useEffect(() => {
    fetchCampaignPerformance();
    fetchTopCampaigns();
    fetchAutomationPerformance();
    fetchTopAutomations();
  }, [fetchCampaignPerformance, fetchTopCampaigns, fetchAutomationPerformance, fetchTopAutomations, selectedRange]);

  // Prepare chart data from automation performance
  const getAutomationChartData = useCallback(() => {
    if (automationPerformance.length === 0) {
      return {
        options: {
          chart: { type: 'bar', toolbar: { show: false } },
          plotOptions: { bar: { columnWidth: '25%', borderRadius: 4 } },
          xaxis: {
            categories: [],
            labels: { rotate: -45, style: { fontSize: '12px', colors: '#9FA9B2' } },
          },
          yaxis: {
            labels: { formatter: (val: number) => `${val.toFixed(1)}%` },
            max: 100,
          },
          colors: ['#008299'],
        } as ApexCharts.ApexOptions,
        series: [
          { name: 'Open Rate', data: [] },
        ],
      };
    }

    // Use real data from API - value is the open rate (0.666... = 66.6%)
    const categories = automationPerformance.map(a => a.name || a.id);
    const data = automationPerformance.map(a => {
      // Convert value to percentage (0.666... -> 66.6)
      return a.value * 100;
    });

    const maxValue = Math.max(...data);

    return {
      options: {
        chart: { type: 'bar', toolbar: { show: false } },
        plotOptions: {
          bar: {
            columnWidth: '25%',
            borderRadius: 4,
            dataLabels: {
              position: 'top'
            }
          }
        },
        dataLabels: {
          enabled: true,
          offsetY: -20,
          style: {
            fontSize: '12px',
            colors: ["#304758"]
          },
          formatter: function (val: number) {
            return val.toFixed(1) + '%';
          }
        },
        xaxis: {
          categories: categories,
          labels: { rotate: -45, style: { fontSize: '12px', colors: '#9FA9B2' } },
        },
        yaxis: {
          labels: { formatter: (val: number) => `${val.toFixed(1)}%` },
          max: Math.max(100, maxValue) + 10,
        },
        colors: ['#008299'],
        grid: { borderColor: '#f1f1f1' },
      } as ApexCharts.ApexOptions,
      series: [
        { name: 'Open Rate', data: data },
      ],
    };
  }, [automationPerformance]);

  // Prepare chart data from campaign performance
  const getCampaignChartData = useCallback(() => {
    if (campaignPerformance.length === 0) {
      return {
        options: {
          chart: { type: 'bar', toolbar: { show: false } },
          plotOptions: { bar: { columnWidth: '15%', borderRadius: 4 } },
          xaxis: {
            categories: [],
            labels: { rotate: -45, style: { fontSize: '12px', colors: '#9FA9B2' } },
          },
          yaxis: {
            labels: { formatter: (val: number) => `${val.toFixed(1)}%` },
            max: 100,
          },
          colors: ['#008299'],
        } as ApexCharts.ApexOptions,
        series: [
          { name: 'Open Rate', data: [] },
        ],
      };
    }

    // Use real data from API - value is the open rate (0.666... = 66.6%)
    const categories = campaignPerformance.map(c => c.name || c.id);
    const data = campaignPerformance.map(c => {
      // Convert value to percentage (0.666... -> 66.6)
      return c.value * 100;
    });

    const maxValue = Math.max(...data);

    return {
      options: {
        chart: { type: 'bar', toolbar: { show: false } },
        plotOptions: {
          bar: {
            columnWidth: '15%',
            borderRadius: 4,
            dataLabels: {
              position: 'top'
            }
          }
        },
        dataLabels: {
          enabled: true,
          offsetY: -20,
          style: {
            fontSize: '12px',
            colors: ["#304758"]
          },
          formatter: function (val: number) {
            return val.toFixed(1) + '%';
          }
        },
        xaxis: {
          categories: categories,
          labels: { rotate: -45, style: { fontSize: '12px', colors: '#9FA9B2' } },
        },
        yaxis: {
          labels: { formatter: (val: number) => `${val.toFixed(1)}%` },
          max: Math.max(100, maxValue) + 10,
        },
        colors: ['#008299'],
        grid: { borderColor: '#f1f1f1' },
      } as ApexCharts.ApexOptions,
      series: [
        { name: 'Open Rate', data: data },
      ],
    };
  }, [campaignPerformance]);

  // Prepare top performing items for automation
  const getAutomationTopPerforming = useCallback((): TopPerformingItem[] => {
    if (topAutomations.length === 0) {
      return [];
    }

    return topAutomations.map(item => ({
      title: item.name,
      rate: `${(item.value * 100).toFixed(1)}%`,
      id: item.id,
      value: item.value,
    }));
  }, [topAutomations]);

  // Prepare top performing items for campaign
  const getCampaignTopPerforming = useCallback((): TopPerformingItem[] => {
    if (topCampaigns.length === 0) {
      return [];
    }

    return topCampaigns.map(campaign => ({
      title: campaign.name,
      rate: `${campaign.openRate.toFixed(1)}%`,
      id: campaign.campaignId,
      value: campaign.openRate,
    }));
  }, [topCampaigns]);

  const automationChartData = getAutomationChartData();
  const campaignChartData = getCampaignChartData();
  const automationTopPerforming = getAutomationTopPerforming();
  const campaignTopPerforming = getCampaignTopPerforming();

  // Combined loading states
  const isCampaignLoading = performanceLoading || topLoading;
  const isAutomationLoading = automationLoading || topAutomationsLoading;

  // Get the current chart data based on active tab
  const getCurrentChartData = () => {
    if (activeTab === 'automation') {
      return automationChartData;
    }
    return campaignChartData;
  };

  // Get the current top performing items based on active tab
  const getCurrentTopPerforming = () => {
    if (activeTab === 'automation') {
      return automationTopPerforming;
    }
    return campaignTopPerforming;
  };

  const currentChartData = getCurrentChartData();
  const currentTopPerforming = getCurrentTopPerforming();

  // Check if current tab has data
  const hasData = activeTab === 'automation'
    ? automationPerformance.length > 0
    : campaignPerformance.length > 0;

  const hasTopData = activeTab === 'automation'
    ? topAutomations.length > 0
    : topCampaigns.length > 0;

  // Render loading state
  const isLoading = activeTab === 'automation' ? isAutomationLoading : isCampaignLoading;

  if (isLoading) {
    return (
      <div className='grid grid-cols-1 md:grid-cols-3 gap-4 mt-6'>
        <div className='md:col-span-2'>
          <div className='bg-white p-4 rounded-md shadow-sm border h-full'>
            <div className='h-8 bg-gray-200 rounded w-64 mb-4 animate-pulse'></div>
            <div className='h-80 bg-gray-200 rounded animate-pulse'></div>
          </div>
        </div>
        <div className='md:col-span-1'>
          <div className='bg-white p-4 rounded-md shadow-sm border h-full'>
            <div className='h-8 bg-gray-200 rounded w-48 mb-6 animate-pulse'></div>
            <div className='space-y-4'>
              {[1, 2, 3].map((i) => (
                <div key={i} className='flex items-center justify-between'>
                  <div className='h-10 w-32 bg-gray-200 rounded animate-pulse'></div>
                  <div className='h-10 w-20 bg-gray-200 rounded animate-pulse'></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Render error state
  const hasError = activeTab === 'automation'
    ? (automationError || topAutomationsError)
    : (performanceError || topError);

  if (hasError) {
    return (
      <div className='grid grid-cols-1 md:grid-cols-3 gap-4 mt-6'>
        <div className='md:col-span-3'>
          <div className='bg-red-50 border border-red-200 rounded-md p-4 text-center'>
            <p className='text-red-600'>
              {activeTab === 'automation'
                ? (automationError || topAutomationsError || 'Unable to load automation data')
                : (performanceError || topError || 'Unable to load campaign data')}
            </p>
            <button
              onClick={() => {
                if (activeTab === 'automation') {
                  fetchAutomationPerformance();
                  fetchTopAutomations();
                } else {
                  fetchCampaignPerformance();
                  fetchTopCampaigns();
                }
              }}
              className='mt-2 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700'
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className='grid grid-cols-1 md:grid-cols-3 gap-4 mt-6'>
      {/* Left Section: Chart */}
      <div className='md:col-span-2 flex flex-col'>
        {/* Toggle Tabs and Filter */}
        <div className='flex justify-between items-center mb-2'>
          <div className='flex gap-2'>
            <button
              onClick={() => setActiveTab('automation')}
              className={`px-4 py-1 rounded-md font-medium ${activeTab === 'automation'
                ? 'bg-[#E8F4FA] text-[#008299]'
                : 'bg-gray-100 text-gray-500'
                }`}
            >
              Automation
            </button>
            <button
              onClick={() => setActiveTab('campaign')}
              className={`px-4 py-1 rounded-md font-medium ${activeTab === 'campaign'
                ? 'bg-[#E8F4FA] text-[#008299]'
                : 'bg-gray-100 text-gray-500'
                }`}
            >
              Campaign
            </button>
          </div>
          <select
            className='border rounded p-2 text-[14px] border-[#E1EAF0] bg-[#F5F8FA] text-[#3F5265] cursor-pointer hover:bg-[#E1EAF0] transition-colors'
            value={selectedRange}
            onChange={(e) => setSelectedRange(e.target.value as 'LAST1D' | 'LAST7D' | 'LAST30D')}
          >
            <option value='LAST1D'>Last 24 Hours</option>
            <option value='LAST7D'>Last 7 Days</option>
            <option value='LAST30D'>Last 30 Days</option>
          </select>
        </div>
        <div className='bg-white p-4 rounded-md shadow-sm border h-full'>
          <h3 className='font-semibold text-[#262626] text-[20px] mb-3'>
            Individual {activeTab === 'automation' ? 'Automation' : 'Campaign'} Performance
          </h3>
          <div className='h-[400px]'>
            {hasData ? (
              <Chart
                options={currentChartData.options}
                series={currentChartData.series}
                type='bar'
                height='100%'
              />
            ) : (
              <div className='h-full flex items-center justify-center text-gray-500'>
                No {activeTab} performance data available
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Right Section: Top Performing Items */}
      <div className='md:col-span-1 flex flex-col'>
        <div className='flex gap-2 mb-2 invisible'>
          <button className={`px-4 py-1 `}>placeholder</button>
          <button className={`px-4 py-1 `}>placeholder</button>
        </div>
        <div className='bg-white p-4 rounded-md shadow-sm border h-full flex flex-col'>
          <h3 className='font-semibold text-[#262626] text-[20px] mb-3'>
            Top Performing {activeTab === 'automation' ? 'Automation' : 'Campaign'}
          </h3>

          {hasTopData ? (
            <ul className='space-y-3 text-sm text-gray-700'>
              {currentTopPerforming.slice(0, 4).map((item, idx) => (
                <li key={item.id || idx} className='flex justify-between items-center'>
                  <div>
                    <p className='text-[#1F2937] text-[14px] font-semibold'>
                      {item.title}
                    </p>
                    <p className='text-[12px] text-[#9FA9B2]'>Newsletter</p>
                  </div>
                  <div className='text-right'>
                    <p className='font-semibold text-[#000000] text-[14px]'>
                      {item.rate}
                    </p>
                    <p className='text-[12px] text-[#9FA9B2]'>Open Rate</p>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <div className='flex items-center justify-center h-full text-gray-500'>
              No top performing data available
            </div>
          )}

          {hasTopData && currentTopPerforming.length > 5 && (
            <p className='mt-auto pt-4 text-sm text-[#4A90E2] cursor-pointer font-semibold text-center hover:underline'>
              View All
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default AutomationSection;